/**
 * Flight color palette and heatmap rendering configuration.
 */

export const DEFAULT_FLIGHT_COLORS: string[] = [
  '#FF3B30',
  '#007AFF',
  '#FF9500',
  '#AF52DE',
  '#00C7BE',
];

/** ⚙️ Heatmap rendering parameters — adjust to tune visual output. */
export const HEATMAP_CONFIG = {
  opacity: 0.7,
  radius: 20,
  intensity: 1.0,
  darkColorFactor: 0.6,
};

/**
 * Returns the hex color for a given palette slot (0-indexed).
 * Falls back to the default palette if custom colors are not provided.
 * Used internally by `getFlightColor` (appStore.ts) once a flight's color
 * slot has been resolved via `flightColorIndex`, and by SelectionModal to
 * preview colors for not-yet-committed pending flights.
 * @param index - 0-indexed palette slot
 * @param customColors - optional user-defined palette (5 hex strings)
 */
export function colorForSlot(index: number, customColors?: string[]): string {
  const palette = customColors ?? DEFAULT_FLIGHT_COLORS;
  return palette[index] ?? DEFAULT_FLIGHT_COLORS[index];
}

/**
 * Returns a darker variant of a hex color by multiplying each channel by `factor`.
 * @param hex - 6-digit hex color string (e.g. '#FF3B30')
 * @param factor - multiplier, defaults to HEATMAP_CONFIG.darkColorFactor
 */
export function darkenColor(hex: string, factor = HEATMAP_CONFIG.darkColorFactor): string {
  const clean = hex.replace('#', '');
  const r = Math.round(parseInt(clean.slice(0, 2), 16) * factor);
  const g = Math.round(parseInt(clean.slice(2, 4), 16) * factor);
  const b = Math.round(parseInt(clean.slice(4, 6), 16) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
